import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useEffect } from 'react';
import styled from 'foundations-theming/utils';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import ShareButton from '../ShareButton/ShareButton';
import { useAbstractDropdownContext } from '../AbstractDropdown/AbstractDropdownProvider';
import { withCssStyles } from '../../../utils/withCssStyles/withCssStyles';
import { filterProps } from '../../../utils/filterProps';
import { getFocusStyleObject } from '../../utils/focus/focus.style';

/**
 * Determine the proper tabIndex based on the `disabled` and `tabIndex` props. Note that `tabIndex`
 * should not have a default value; it should only be non-null to override normal tabbing behavior.
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const getTabIndex = (disabled, tabIndex, disabledValue = -1) => {
  if (typeof tabIndex === 'number') return tabIndex;
  if (disabled) {
    if (disabledValue === null) return undefined;
    return disabledValue;
  }
  return 0;
};

// Omits properties that aren't included in `HTMLElementTagNameMap & SVGElementTagNameMap` (this means that we don't support the omitted elements)

// Omits properties that aren't included in `JSX.IntrinsicElements` (this means that we don't support the omitted elements)

// Includes all available props except for `tagName`

// This type is used when a `tagName` is passed to the component

// This type is used when no `tagName` is passed to the component (defaults to a div)

// The "official" type of the component's props

// This is how TS picks the correct prop types to use (depends on whether a `tagName` is provided or not)

// The generic type `T` represents the HTML tag name that is used to render the component
function ClickableCore(_ref) {
  let {
      tagName,
      // The default value here is "div", but the default value is attached to the `TagAsComponent` variable and the `ClickablePropsWithDefaultTagName` type (this is how we need to do it when working with a generic type that comes from `tagName`)
      buttonRef,
      role = 'button',
      autoFocus,
      // Allow className to pass through because Clickable is one of the few components
      // we still allow wrapping in styled() which requires a className to attach to DOM elements
      // @see https://styled-components.com/docs/advanced#styling-normal-react-components
      className,
      disabled = false,
      onClick,
      onKeyDown,
      tabIndex,
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, ["tagName", "buttonRef", "role", "autoFocus", "cursor", "className", "disabled", "onClick", "onKeyDown", "tabIndex", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
  const TagAsComponent = tagName || 'div';

  /**
   * Remove style/token props from `rest` otherwise they will cause an Unknown Prop Warning
   * @see https://react.dev/warnings/unknown-prop
   */
  const {
    domProps,
    callbackProps,
    restProps
  } = filterProps(rest, {
    disallowedProps: []
  });
  const filteredRest = Object.assign({}, domProps, callbackProps, Object.fromEntries(Object.entries(restProps).filter(([key]) => !key.startsWith('$'))));
  useEffect(() => {
    if (autoFocus && buttonRef && buttonRef.current) buttonRef.current.focus();
  }, [autoFocus, buttonRef]);

  // Can't find an existing TS type that maps each tag name to its corresponding element type (e.g. {div: HTMLDivElement; span: HTMLSpanElement}), so the `KeyboardEvent` type accepts `any`
  const handleKeyDown = evt => {
    const {
      key
    } = evt;
    const target = evt.target;
    if (onKeyDown) onKeyDown(evt);

    // Ignore keydown events that bubbled up
    if (buttonRef && target !== buttonRef.current) return;

    // Simulate click on Enter/Spacebar, to emulate button behavior
    if (key === 'Enter' || key === ' ') {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      if (onClick) {
        // @ts-ignore Keyboard events and mouse events do not have the same object shape
        onClick(evt);
      }
      if (key === ' ') {
        evt.preventDefault(); // Prevent page from scrolling (#3575)
      }
    }
  };

  // Can't find an existing TS type that maps each tag name to its corresponding element type (e.g. {div: HTMLDivElement; span: HTMLSpanElement}), so the `MouseEvent` type accepts `any`
  const handleClick = evt => {
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    if (onClick) onClick(evt);
  };
  return /*#__PURE__*/_jsx(TagAsComponent, Object.assign({}, filteredRest, {
    role: role,
    "aria-disabled": disabled,
    className: className,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    ref: buttonRef,
    tabIndex: getTabIndex(disabled, tabIndex, null),
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'Clickable'
  }));
}
export const StyledClickable = styled(ClickableCore)(({
  $cssStyles,
  cursor,
  disabled,
  theme
}) => {
  const abstractDropdownContext = useAbstractDropdownContext();
  return Object.assign({
    cursor: disabled ? 'not-allowed' : cursor !== undefined ? cursor : 'pointer'
  }, $cssStyles, abstractDropdownContext === null || abstractDropdownContext === void 0 ? void 0 : abstractDropdownContext.buttonStyleOverrides, getFocusStyleObject({
    theme
  }));
});
export const Clickable = withCssStyles(ShareButton(StyledClickable));